import { useEffect, useRef } from "react"
import { api } from "./api"
import { useApp, useRealtimeStore } from "./store"

/* Live SSE liveness layer.
   --------------------------------------------------------------------------
   This is a PULSE layer that sits on top of the existing 45s react-query
   poll — it never replaces the data fetch. We open one EventSource to the
   backend's native `/realtime` stream and, for every well-formed envelope,
   bump the realtime store's pulse counters so the cockpit can flash subtly.

   Design constraints (demo/offline must stay calm):
     - Never throws — a failed connect or a malformed frame is swallowed.
     - Never spams the console — no error logging on the (expected) offline path.
     - Reconnects with capped exponential backoff, cleaning up on unmount.
     - No-ops gracefully when EventSource is unavailable or the stream never
       connects: the header dot simply stays in its poll-derived state. */

export interface RealtimeEnvelope<T = unknown> {
  topic: string
  payload: T
  publishedAt?: string
  at?: string
  traceId?: string
}

/** Default topics the cockpit cares about; falls back to the server default
    (`module.*.signal.*`) shape implicitly when the query string is dropped. */
export const DEFAULT_REALTIME_TOPICS = [
  "disruption-radar.signal.incident_created",
  "cockpit.signal.correlation_new",
  "alerts.signal.scheduled_fired",
] as const

const INITIAL_BACKOFF_MS = 1_000
const MAX_BACKOFF_MS = 30_000

/** Open the realtime stream and feed pulse events into the store.
    Returns nothing — consumers read liveness via `useRealtimeStore`. */
export function useRealtime(
  topics: readonly string[] = DEFAULT_REALTIME_TOPICS,
): void {
  const setConnection = useApp((s) => s.setConnection)
  const notePulse = useRealtimeStore((s) => s.notePulse)

  // Stash the topic list as a stable string so the effect re-subscribes only
  // when the actual set of topics changes, not on every render.
  const topicsKey = topics.join(",")

  useEffect(() => {
    // SSR / very old browsers: no EventSource → silently do nothing.
    if (typeof window === "undefined" || typeof EventSource === "undefined") {
      return
    }

    let source: EventSource | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let backoff = INITIAL_BACKOFF_MS
    let disposed = false

    const url = `${api.baseUrl}/realtime?topics=${encodeURIComponent(topicsKey)}`

    const handleMessage = (msg: MessageEvent<string>) => {
      let raw: RealtimeEnvelope
      try {
        raw = JSON.parse(msg.data) as RealtimeEnvelope
      } catch {
        return // malformed frame — ignore
      }
      if (!raw || typeof raw.topic !== "string") return
      notePulse({
        topic: raw.topic,
        at: raw.at ?? raw.publishedAt ?? new Date().toISOString(),
      })
    }

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer) return
      const delay = backoff
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS)
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        connect()
      }, delay)
    }

    const connect = () => {
      if (disposed) return
      setConnection("connecting")
      let es: EventSource
      try {
        es = new EventSource(url, { withCredentials: true })
      } catch {
        // Construction itself failed (bad URL / blocked) — back off quietly.
        scheduleReconnect()
        return
      }
      source = es

      es.onopen = () => {
        if (disposed) return
        backoff = INITIAL_BACKOFF_MS // healthy connection resets the backoff
        setConnection("connected")
      }
      es.onmessage = handleMessage
      es.onerror = () => {
        if (disposed) return
        setConnection("disconnected")
        // EventSource auto-retries, but its cadence is opaque and it keeps the
        // socket churning; close and drive reconnection ourselves with backoff.
        es.close()
        if (source === es) source = null
        scheduleReconnect()
      }
    }

    connect()

    return () => {
      disposed = true
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      if (source) {
        source.close()
        source = null
      }
      setConnection("disconnected")
    }
    // topicsKey captures the topic set; notePulse/setConnection are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicsKey])
}
